import React from 'react';
import { assert } from '../base/utils/assert';
import pivot from 'data-pivot';

import ServerIO from '../base/plumbing/ServerIOBase';
import DataStore from '../base/plumbing/DataStore';
import Misc from '../base/components/Misc';
import md5 from 'md5';
import SimpleTable from '../base/components/SimpleTable';
import {displayValue, DonationDataDisplay, pastCalendarDate, typeFromParam} from './CommonTrafficReport';
import PropControl from '../base/components/PropControl';

// which stats do we care about? Anything not in here will be filtered out of data returned
const FIELDS_IN_USE = ["render", "open", "endvideo", "minview", "donation", "click"];
const filtersPath = ["widget", "vertdash", "filters"];

function AdvertiserTrafficReport() {
	assert(DataStore.getValue('location', 'path')[1], 'PubTrafficReport.jsx - no ad in path');
	const q = DataStore.getValue('location', 'path')[1];
	const start = DataStore.getUrlValue('start') || pastCalendarDate(0, 1).toISOString();
	const end = DataStore.getUrlValue('end');
	const filters = {start, end};

	// set initial filter values
	if( ! DataStore.getValue(filtersPath) ) {
		setFilters(filters);
		DataStore.setValue(filtersPath, filters);
	}

	const {pgldata, breakdownData, derivedValues, spendData, donationsdataByCharity} = dataFetchWrapper({q, start, end});

	// no data
	if ( ! pgldata) {
		return (
			<div className="PubTrafficReport">
				<h3>Fetching your data...</h3>
				<Misc.Loading />
			</div>
		);
	}

	return (
		<div className="PubTrafficReport">
			<TimeLineControls />
			<Misc.CardAccordion>
				<ReportWidget title={'Stats'}>
					<BreakdownWidget data={breakdownData} param="Stats" />
				</ReportWidget>
				<ReportWidget title={'Key performance indicators'} >
					<BreakdownWidget data={derivedValues} param="Key performance indicators" />
				</ReportWidget>
				<ReportWidget title={'Budget'} >
					<BudgetBreakdown data={spendData} />
				</ReportWidget>
				<ReportWidget title={'Charity breakdown'}>
					<DonationDataDisplay data={donationsdataByCharity} />
				</ReportWidget>
			</Misc.CardAccordion>
		</div>
	);
} // ./PubTrafficReport

function ReportWidget({ children, ...stuff }) {
  return <Misc.Card {...stuff}>
		{children}
	</Misc.Card>
}
// ./ReportWidget

function BreakdownWidget({data, param}) {
	//assMatch(param, String);
	if ( ! data) return null;

	let type = typeFromParam(param);
	let columns = [
		{
			Header: type || param,
			accessor: 'key',
			Cell: k => {
				let nk = k;
				let item = type && DataStore.getValue('data', type, k);
				if (item) nk = item.name || k;
				return nk;
			}
		},
		{
			Header: 'Amount',
			accessor: 'value',
			Cell: displayValue
		}
	];
	return <div className="well-lg"><SimpleTable columns={columns} dataObject={data} addTotalRow csv /></div>;
}

const dataFetchWrapper = ({q, start, end}) => {
	let dspec = md5(JSON.stringify({q, start, end}));
	const glpath = ['widget', 'TrafficReport', 'gl', dspec];
	const evtspbPath = ['widget', 'TrafficReport', 'evtspb', dspec];
	const moneyPath = ['widget', 'TrafficReport', 'money', dspec];
	const donationspath = ['widget', 'PubTrafficReport', 'donations', dspec];
	const params = {q: 'vert:' + q, dataspace: 'gl', start, end};

	let pgldata = DataStore.fetch(glpath, () => {
		// Where does ad activity data go??
		// dataspaces: trk for evt.pxl, goodloop for evt.viewable, adview, click, close, open, opened
		// money for spending
		// see https://github.com/good-loop/doc/wiki/Canonical-Terminology-for-Logging-Good-Loop-Events
		let breakdowns = ['evt/vert'];
		return ServerIO.getDataLogData({...params, breakdowns, name: 'all-events'})
			.then(res => res.cargo);
	});

	let evtsByPub = DataStore.fetch(evtspbPath, () => {
		let breakdowns = ['evt/pub', 'pub'];
		return ServerIO.getDataLogData({...params, breakdowns, name: 'all-events-by-pub'})
			.then(res => res.cargo);
	});

	const gldata = pgldata.value;
	const breakdownByPub = evtsByPub.value;
	let evthostdata0;
	let evthostdata;
	let breakdownData;
	let derivedValues;

	if(gldata) {
		// The table data
		evthostdata0 = pivot(gldata, 'by_evt_vert.buckets.$bi.{key, by_vert.buckets.$bi2.$bucket}',
			"$key.$bi2.$bucket");
		evthostdata = pivot(evthostdata0, "$evtkey.$bi2.{key, doc_count}",
			"$evtkey.$key.$doc_count");
		breakdownData = pivot(evthostdata, "$evtkey.$bi2.$v", "$evtkey.$v");

		// filter out fields not used in Ryan's reports to eliminate clutter
		breakdownData = FIELDS_IN_USE.reduce((obj, e) => {
			obj[e] = breakdownData[e] || 0;
			return obj;
		}, {});

		derivedValues = {
			"Banner-open rate": `${parseFloat((breakdownData.open / breakdownData.render) * 100).toFixed(2)}%`,
			"Completed rate": `${parseFloat((breakdownData.endvideo / breakdownData.open) * 100).toFixed(2)}%`,
			"Charity Engagement": `${parseFloat((breakdownData.donation / breakdownData.endvideo) * 100).toFixed(2)}%`,
			"Click-through rate": `${parseFloat((breakdownData.click / breakdownData.endvideo) * 100).toFixed(2)}%`
		};
	}

	let pvMoneyData = DataStore.fetch(moneyPath, () => {
		return ServerIO.getSpendData({q: 'vert:' + q, breakdown: 'vert', start, end});
	});
	const moneyFromHost = pvMoneyData && pvMoneyData.value && pvMoneyData.value.total && pvMoneyData.value.total.value ? pvMoneyData.value.total.value : null;

	let pvDonationsData = DataStore.fetch(donationspath, () => {
		return ServerIO.getDonationsData({q: 'vert:'+q, start, end})
			.then(res => res.data);
	});
	let donationsdataByCharity = pvDonationsData.value;
	
	let spendData;
	if(moneyFromHost && breakdownData && donationsdataByCharity) {
		const spend = moneyFromHost;
		spendData = {
			"Budget spend": spend,
			"Donations raised": donationsdataByCharity.total && donationsdataByCharity.total.value ? donationsdataByCharity.total.value : 0,
			"Cost per impression": spend / breakdownData.render,
			"Cost per engagement": spend / breakdownData.open,
			"Cost per completed view": spend / breakdownData.endvideo
		};
	}

	return {breakdownByPub, pgldata, evthostdata, breakdownData, derivedValues, donationsdataByCharity, spendData};
};

/**
 * 
 * @param data {"Title" : amount}
 */
function BudgetBreakdown({data}) {
	if(!data) return null;

	const headers = Object.keys(data);
	return (
		<div>
			{headers.map(e => (
				<div key={e}>
					<strong>{`${e}: `}</strong>
					<Misc.Money amount={data[e]} />
				</div>))}
		</div>
	);
}

// click -> copy the settings into the url
const setFilters = (filters) => {
	Object.keys(filters).map( p => DataStore.setUrlValue(p, filters[p]));
};

function TimeLineControls() {
	const filters = DataStore.getValue(filtersPath);

	return (
		<div className="well">
			<PropControl path={filtersPath} prop="start" label="Start" />
			<PropControl path={filtersPath} prop="end" label="End" />
			<button className="btn btn-primary" onClick={() => setFilters(filters)}>Load Data</button>
		</div>
	);
}

export default AdvertiserTrafficReport;
